'use client';
import { Box } from '@mui/material';
import { forwardRef } from 'react';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { FilteredReviewContentSectionOffer } from '@repo/supergraph/review/bankingPage/types';
import { CtaButton } from '@repo/ui/CtaButton';
import { CtaVariantsEnum } from '../../../../../CtaButton/variants';
import { StarRating } from '../../../shared/StarRating';
import type { DriverGroup } from '../../utils';
import { DriverTableCell } from './DriverTableCell';
import { HeaderCells } from './HeaderCells';

type ColumnContentProps = {
  marketplaceEntity: FilteredReviewContentSectionOffer;
  columnIndex: number;
  groupedDrivers: DriverGroup[][];
  totalRows: number;
  isCurrentPageInstitution?: boolean;
};

export const ColumnContent = forwardRef<HTMLDivElement, ColumnContentProps>(
  (
    { marketplaceEntity, columnIndex, groupedDrivers, totalRows, isCurrentPageInstitution = false },
    ref,
  ) => {
    if (!marketplaceEntity) {
      return null;
    }

    const { starRating, starRatingTooltip } = marketplaceEntity;
    const overrideCta = marketplaceEntity.ctaOverride ?? marketplaceEntity.cta;
    const shouldNotDisplayCta =
      isCurrentPageInstitution && overrideCta?.type !== EntityPlacementCtaTypeEnum.ApplyNow;

    return (
      <>
        <HeaderCells
          marketplaceEntity={marketplaceEntity}
          columnIndex={columnIndex}
          innerRef={ref}
          shouldNotDisplayCta={shouldNotDisplayCta}
        />
        <Box
          sx={{
            gridColumn: columnIndex + 1,
            display: 'flex',
            gridRow: 3,
            flexDirection: 'column',
            px: 2,
            pt: '20px',
            borderLeft: columnIndex === 0 ? '0px' : '1px solid',
            borderLeftColor: columnIndex === 0 ? 'transparent' : 'neutral.lighter',
            borderTop: '1px solid',
            borderTopColor: 'neutral.lighter',
            borderBottom: '1px solid',
            borderBottomColor: 'neutral.lighter',
            alignItems: 'center',
          }}
        >
          <StarRating
            value={starRating}
            layout="column"
            showLabel
            tooltipText={starRatingTooltip}
            withBackground={false}
            href={isCurrentPageInstitution ? undefined : marketplaceEntity.reviewLink}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          />
          <Box
            sx={{
              pt: '20px',
            }}
          />
        </Box>

        {groupedDrivers.map((driversGroup, groupIndex) => {
          const cell = driversGroup[columnIndex];

          return (
            <DriverTableCell
              key={`${groupIndex}-${columnIndex}`}
              cell={cell}
              groupIndex={groupIndex}
              columnIndex={columnIndex}
              driversLength={groupedDrivers.length}
            />
          );
        })}

        <Box
          sx={{
            gridColumn: columnIndex + 1,
            gridRow: totalRows,
            display: 'flex',
            flexDirection: 'column',
            pt: { xs: '1.25rem', sm: 3 },
            pb: 3,
            px: { xs: 2, sm: 3 },
            borderLeft: columnIndex === 0 ? '0px' : '1px solid',
            borderLeftColor: columnIndex === 0 ? 'transparent' : 'neutral.lighter',
            borderTop: '1px solid',
            borderTopColor: 'neutral.lighter',
          }}
        >
          {!shouldNotDisplayCta && (
            <CtaButton
              cta={overrideCta}
              variant={CtaVariantsEnum.Primary}
              analyticsPayload={{
                index: columnIndex,
                institutionId: marketplaceEntity.institution.id,
                offerId: marketplaceEntity.id,
                productId: marketplaceEntity.product.id,
                productType: marketplaceEntity.productType,
                monetizable: marketplaceEntity.monetizable,
              }}
              subtextStylesOverride={{
                marginTop: 2,
                fontSize: 11,
                display: 'block',
                color: 'black',
              }}
              columnIndex={columnIndex}
            />
          )}
        </Box>
      </>
    );
  },
);
